'use client';

import { useSyncExternalStore, useState } from 'react';

import { motion } from 'framer-motion';
import { useTranslations } from 'use-intl';
import { z } from 'zod';

import { needsManualTabSelection } from '@/shared-utilities';
import { GetProjectQuery } from '@/survey-graphql';

import ProjectUsabilityHandsFreeStuckFormStep from './project-usability-handsfree-stuck-form-step';
import ProjectUsabilityHandsFreeThankYouStep from './project-usability-handsfree-thank-you-step';
import ProjectUsabilityHandsFreeWelcomeStep from './project-usability-handsfree-welcome-step';

type FlowStep = 'welcome' | 'stuck-form' | 'thank-you';

interface Props {
  projectId: string;
  orgSettings?: GetProjectQuery['projectOrgSettings'];
  onContinue: () => void;
  isLoading?: boolean;
  showCameraSetup?: boolean;
}

function emptySubscribe() {
  return () => {
    /* no-op unsubscribe */
  };
}

function useIsClient() {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false
  );
}

function useClientSideTabSelectionHint() {
  const isClient = useIsClient();
  const needsHint = isClient ? needsManualTabSelection() : false;

  return { showHint: needsHint, mounted: isClient };
}

export default function ProjectUsabilityHandsFreeWelcomeModal({
  projectId,
  onContinue,
  isLoading = false,
  showCameraSetup = true,
}: Props) {
  const t = useTranslations('UserTest');
  const tUsability = useTranslations('Usability');

  const { showHint: showTabSelectionHint, mounted } =
    useClientSideTabSelectionHint();
  const [flowStep, setFlowStep] = useState<FlowStep>('welcome');
  const [problem, setProblem] = useState('');
  const [email, setEmail] = useState('');
  const [problemError, setProblemError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const showStuckFlow = flowStep !== 'welcome';

  const handleStuckClick = () => {
    setFlowStep('stuck-form');
  };

  const handleGoBack = () => {
    setFlowStep('welcome');
    setProblemError(undefined);
    setEmailError(undefined);
  };

  const handleProblemChange = (value: string) => {
    setProblem(value);
    setProblemError(undefined);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError(undefined);
  };

  const handleFormContinue = () => {
    const stuckFormSchema = z.object({
      problem: z.string().min(1, t('stuck.problemRequired')),
      email: z.email(t('stuck.emailInvalid')),
    });
    const result = stuckFormSchema.safeParse({
      problem: problem.trim(),
      email: email.trim(),
    });
    if (!result.success) {
      const tree = z.treeifyError(result.error);
      setProblemError(tree.properties?.problem?.errors?.[0]);
      setEmailError(tree.properties?.email?.errors?.[0]);
      return;
    }
    const subject = encodeURIComponent(t('stuck.emailSubject'));
    const body = encodeURIComponent(
      `${tUsability('mailtoProblemLabel')} ${result.data.problem}\n\n${tUsability('mailtoEmailLabel')} ${result.data.email}`
    );
    window.location.href = `mailto:support@theysaid.io?subject=${subject}&body=${body}`;
    setFlowStep('thank-you');
  };

  if (!mounted) {
    return null;
  }

  if (flowStep === 'thank-you') {
    return (
      <div
        data-test='handsfree-welcome-modal'
        className='fixed inset-0 z-50 flex items-center justify-center p-6'
      >
        <div className='mx-4 w-full max-w-md shrink-0 rounded-2xl bg-white shadow-xl'>
          <ProjectUsabilityHandsFreeThankYouStep />
        </div>
      </div>
    );
  }

  return (
    <div
      data-test='handsfree-welcome-modal'
      className='fixed inset-0 z-50 flex items-center justify-center bg-gray-950/20 p-6'
    >
      <div className='relative mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl'>
        <motion.div
          className='flex w-[200%]'
          animate={{ x: showStuckFlow ? '-50%' : '0%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div className='w-1/2'>
            <ProjectUsabilityHandsFreeWelcomeStep
              projectId={projectId}
              showTabSelectionHint={showTabSelectionHint}
              onStuckClick={handleStuckClick}
              onContinue={onContinue}
              isLoading={isLoading}
              showCameraSetup={showCameraSetup}
            />
          </div>
          <div className='w-1/2'>
            <ProjectUsabilityHandsFreeStuckFormStep
              problem={problem}
              email={email}
              problemError={problemError}
              emailError={emailError}
              onProblemChange={handleProblemChange}
              onEmailChange={handleEmailChange}
              onGoBack={handleGoBack}
              onContinue={handleFormContinue}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
